import { useQuery } from "@tanstack/react-query";
import schedulerApi from "../../../services/api/SchedulerApiService";
import { useAuthStore } from "../../zustand";

/**
 * Custom hook for fetching and caching video library data
 *
 * Provides list of available videos for the library/video selection screens.
 * Uses TanStack Query as the single source of truth - no redundant Zustand caching.
 * Data is cached per language to enable instant language switching without API refetch.
 *
 * Cache Strategy:
 * - Query key format: ['videos', email, preferredLanguage]
 * - Separate cache entries per language ensure instant language switching
 * - staleTime: 10 minutes (videos change rarely, less frequently than home data)
 * - gcTime: 15 minutes (keep longer in memory since used less frequently)
 * - Refetch on mount if stale, on network reconnect, but NOT on window focus
 *
 * Video Categories:
 * - allVideos: Complete library of all available videos
 * - physioVideos: Subset of videos specifically for physiotherapy practitioners
 *
 * @returns {Object} Query result object
 * @returns {Object} result.data - {allVideos: [], physioVideos: []}
 * @returns {boolean} result.isLoading - True on first load (no cached data)
 * @returns {boolean} result.isFetching - True while any fetch is in progress
 * @returns {Error} result.error - Error object if fetch failed
 * @returns {Function} result.refetch - Manual refetch function
 */
export const useVideosQuery = () => {
  // Get current user email from auth store (used as cache key dimension)
  const email = useAuthStore((state) => state.user.email);
  // Get user's language preference from auth store (used as cache key dimension for instant switching)
  const preferredLanguage = useAuthStore((state) => state.preferredLanguage);

  /**
   * TanStack Query hook for managing videos fetching and caching
   * Query is disabled until user is authenticated (email exists)
   */
  const query = useQuery({
    // Cache key dimensions: separate caches per user email and language
    // This enables instant language switching by selecting a different cache entry
    queryKey: ["videos", email, preferredLanguage],

    /**
     * Query function: fetch video library data from API
     * @async
     * @returns {Object} Structured data with allVideos and physioVideos arrays
     */
    queryFn: async () => {
      // Fetch complete video library from API in user's language
      const response = await schedulerApi.getLibraryVideos(preferredLanguage);

      // Return videos organized by category for different UI contexts
      return {
        allVideos: response.allVideos || [], // Complete video library for general users
        physioVideos: response.physioVideos || [], // Physiotherapy-specific videos for practitioners
      };
    },

    // Only enable query when user is authenticated (email available from auth store)
    enabled: !!email,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 48 * 60 * 60 * 1000, // 48 hours
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    placeholderData: (previousData) => previousData,
    // Retry failed requests up to 3 times with exponential backoff
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Structural sharing false for performance (videos array is large)
    structuralSharing: false,
  });

  // Return subset of query state useful for UI components
  return {
    data: query.data, // Structured data: {allVideos, physioVideos}
    isLoading: query.isLoading, // True during initial load (no cached data exists)
    isFetching: query.isFetching, // True while any fetch is in progress (including background refetch)
    error: query.error, // Error object if fetch failed, null if successful
    refetch: query.refetch, // Function to manually trigger refetch
  };
};
